import axios from 'axios';
import type { BackendUsersMeResponse, MePayload } from '@/features/auth/types';
import { ChartDataPoint } from '@/ui/chart-card';
import { FR_DAYS_SHORT, FR_MONTHS_SHORT } from '@/ui/constant';
import * as XLSX from 'xlsx';
import {
  ApiActivity,
  Activity,
  ApiNotificationSettings,
  NotificationSettings,
} from '@/features/system-management/components/hook/types';

export interface ExportColumn<T> {
  header: string;
  accessor: keyof T | ((item: T) => string | number);
  width?: number;
}

export interface ExportOptions {
  filename: string;
  sheetName?: string;
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDated(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function padTwo(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Interprète une chaîne API (ISO, date seule YYYY-MM-DD en local, jj/mm/aaaa, etc.).
 */
function parseFlexibleDateTime(input: string | null | undefined): Date | null {
  if (input == null) return null;
  const s = String(input).trim();
  if (!s) return null;

  const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (isoDateOnly) {
    const y = Number(isoDateOnly[1]);
    const m = Number(isoDateOnly[2]) - 1;
    const day = Number(isoDateOnly[3]);
    const d = new Date(y, m, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d;

  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/.exec(s);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]) - 1;
    const year = Number(dmy[3]);
    const dt = new Date(year, month, day);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  return null;
}

function formatLocalDateDdMmYyyy(d: Date): string {
  return `${padTwo(d.getDate())}-${padTwo(d.getMonth() + 1)}-${d.getFullYear()}`;
}

function formatLocalTime24hHmFromDate(d: Date): string {
  return `${padTwo(d.getHours())}-${padTwo(d.getMinutes())}`;
}

/**
 * Date au format jj-mm-aaaa (fuseau local).
 */
export function formatDateDdMmYyyy(input: string | null | undefined): string {
  const d = parseFlexibleDateTime(input);
  return d ? formatLocalDateDdMmYyyy(d) : '';
}

/**
 * Heure au format HH-mm (24 h, fuseau local). Accepte aussi une heure seule « 14:30 » ou « 14-30 ».
 */
export function formatTime24hHm(input: string | null | undefined): string {
  if (input == null) return '';
  const s = String(input).trim();
  if (!s) return '';

  const d = parseFlexibleDateTime(input);
  if (d) return formatLocalTime24hHmFromDate(d);

  const hm = /^(\d{1,2})[::](\d{2})(?::(\d{2}))?$/.exec(s);
  if (hm) return `${padTwo(Number(hm[1]))}:${padTwo(Number(hm[2]))}`;

  return '';
}

function normalizeTimeSegmentForParsing(timeStr: string): string {
  return timeStr.trim().replace(/-/g, ':');
}

/**
 * Libellés date / heure pour un couple { date, time } (ex. destinataires d’un deal).
 * Si seul `date` contient un datetime ISO, la date et l’heure en sont dérivés.
 */
export function formatRecipientSentAtDisplay(
  sentAt: { date?: string; time?: string } | null | undefined
): { date: string; time: string } {
  if (!sentAt) return { date: '', time: '' };
  const rawDate = (sentAt.date ?? '').trim();
  const rawTime = (sentAt.time ?? '').trim();

  if (!rawDate && !rawTime) return { date: '', time: '' };

  if (rawDate && !rawTime) {
    const d = parseFlexibleDateTime(rawDate);
    if (d) {
      const isYyyyMmDdOnly = /^(\d{4})-(\d{2})-(\d{2})$/.test(rawDate.trim());
      return {
        date: formatLocalDateDdMmYyyy(d),
        time: isYyyyMmDdOnly ? '' : formatLocalTime24hHmFromDate(d),
      };
    }
    return { date: rawDate, time: '' };
  }

  if (!rawDate && rawTime) {
    const d = parseFlexibleDateTime(rawTime);
    if (d) {
      return {
        date: formatLocalDateDdMmYyyy(d),
        time: formatLocalTime24hHmFromDate(d),
      };
    }
    return { date: '', time: formatTime24hHm(rawTime) || rawTime };
  }

  const combined = rawDate.includes('T')
    ? rawDate
    : `${rawDate}T${normalizeTimeSegmentForParsing(rawTime)}`;
  let d = parseFlexibleDateTime(combined);
  if (!d) {
    d = parseFlexibleDateTime(
      `${rawDate} ${normalizeTimeSegmentForParsing(rawTime)}`
    );
  }
  if (d) {
    return {
      date: formatLocalDateDdMmYyyy(d),
      time: formatLocalTime24hHmFromDate(d),
    };
  }

  return {
    date: formatDateDdMmYyyy(rawDate) || rawDate,
    time: formatTime24hHm(rawTime) || rawTime,
  };
}

/**
 * Export data to Excel file
 */
export function exportToExcel<T extends Record<string, unknown>>(
  data: T[],
  columns: ExportColumn<T>[],
  options: ExportOptions
): void {
  // Create worksheet data
  const worksheetData: (string | number)[][] = [];

  // Add headers
  const headers = columns.map((col) => col.header);
  worksheetData.push(headers);

  // Add data rows
  data.forEach((item) => {
    const row = columns.map((col) => {
      if (typeof col.accessor === 'function') {
        return col.accessor(item);
      }
      const value = item[col.accessor];
      if (value === null || value === undefined) return '';
      return value as string | number;
    });
    worksheetData.push(row);
  });

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Set column widths
  const colWidths = columns.map((col) => ({
    wch: col.width || 15,
  }));
  worksheet['!cols'] = colWidths;

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    options.sheetName || 'Sheet1'
  );

  // Generate filename with date
  const date = new Date().toISOString().split('T')[0];
  const filename = `${options.filename}_${date}.xlsx`;

  // Download file
  XLSX.writeFile(workbook, filename);
}

/**
 * Export transactions specifically
 */
export function exportTransactionsToExcel(
  transactions: {
    id: string;
    vehicle: string;
    type: 'achat' | 'vente';
    date: string;
    price: number;
    status: 'en-cours' | 'terminee';
  }[]
): void {
  const columns: ExportColumn<(typeof transactions)[0]>[] = [
    { header: 'ID', accessor: 'id', width: 15 },
    { header: 'Véhicule', accessor: 'vehicle', width: 30 },
    {
      header: 'Type',
      accessor: (item) => (item.type === 'achat' ? 'Achat' : 'Vente'),
      width: 10,
    },
    { header: 'Date', accessor: 'date', width: 15 },
    {
      header: 'Prix (CHF)',
      accessor: (item) => item.price,
      width: 15,
    },
    {
      header: 'Statut',
      accessor: (item) =>
        item.status === 'terminee' ? 'Terminée' : 'En cours',
      width: 12,
    },
  ];

  exportToExcel(transactions, columns, {
    filename: 'transactions',
    sheetName: 'Transactions',
  });
}

/**
 * Combine class names conditionally
 * Useful for conditionally applying classes
 *
 * @example
 * cn('base-class', condition && 'conditional-class', 'another-class')
 */
export function cn(...inputs: (string | boolean | undefined | null)[]): string {
  return inputs
    .filter(
      (input): input is string => typeof input === 'string' && input.length > 0
    )
    .join(' ');
}

/**
 * Convertit un montant API (nombre JSON ou chaîne type Decimal Django `"44900.00"`).
 * Sans cela, une chaîne passée à `formatChartNumber` / `formatPrice` affiche « 0 CHF ».
 */
export function coerceApiNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const t = value.trim().replace(/\s/g, '').replace(/'/g, '');
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Montants CHF : milliers séparés par une apostrophe, décimales avec un point (ex. 1'234'567.89).
 */
export function formatChartNumber(
  value: number,
  options?: { fractionDigits?: number }
): string {
  if (!Number.isFinite(value)) return '0';
  const fd = options?.fractionDigits ?? 2;
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  let intPart: string;
  let decPart: string | undefined;
  if (fd <= 0) {
    intPart = String(Math.round(abs));
  } else {
    const fixed = abs.toFixed(fd);
    const parts = fixed.split('.');
    intPart = parts[0];
    decPart = parts[1];
  }
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return decPart !== undefined ? `${sign}${grouped}.${decPart}` : `${sign}${grouped}`;
}

/** Montant graphe + devise CHF (toujours 2 décimales). */
export function formatChartCurrencyCHF(value: number): string {
  return `${formatChartNumber(value, { fractionDigits: 2 })} CHF`;
}

/** Graduations axe Y des graphiques finance (entiers). */
export function formatChartYAxisTick(value: number): string {
  return formatChartNumber(value, { fractionDigits: 0 });
}

/**
 * Montant en CHF (format suisse : apostrophes pour les milliers, point pour les décimales si besoin).
 *
 * @example
 * formatPrice(13000) // "13'000 CHF"
 * formatPrice(undefined) // "-"
 */
export function formatPrice(amount?: number | string | null): string {
  if (amount === undefined || amount === null) return '-';
  const n =
    typeof amount === 'number' ? amount : coerceApiNumber(amount);
  if (n === null) return '-';
  return `${formatChartNumber(n, { fractionDigits: 0 })} CHF`;
}

/**
 * Format a date in French format (e.g., "15 Janvier 2024")
 *
 * @param date - The date to format (can be null)
 * @returns Formatted date string or empty string if null
 *
 * @example
 * formatDate(new Date(2024, 0, 15)) // "15 Janvier 2024"
 */
export function formatDate(date: Date | null): string {
  if (!date) return '';
  const monthNames = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ];
  return `${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * Format a number for Y-axis display (converts to thousands with 'k' suffix)
 *
 * @param tickItem - The number to format
 * @returns Formatted string (e.g., "2k" for 2000)
 *
 * @example
 * formatYAxis(2000) // "2k"
 * formatYAxis(8500) // "8k"
 */
export function formatYAxis(tickItem: number): string {
  return `${tickItem / 1000}k`;
}

/**
 * Toggle the active state of a filter in an array of filters
 *
 * @param filters - Array of filter objects with id and isActive properties
 * @param filterId - The ID of the filter to toggle
 * @param active - The new active state
 * @returns New array with the updated filter
 *
 * @example
 * const filters = [{ id: '1', isActive: true }, { id: '2', isActive: false }];
 * toggleFilter(filters, '1', false) // [{ id: '1', isActive: false }, { id: '2', isActive: false }]
 */
export function toggleFilter<T extends { id: string; isActive: boolean }>(
  filters: T[],
  filterId: string,
  active: boolean
): T[] {
  return filters.map((filter) =>
    filter.id === filterId ? { ...filter, isActive: active } : filter
  );
}

/**
 * Validate an email address using regex pattern
 *
 * @param email - The email address to validate
 * @returns true if the email is valid, false otherwise
 *
 * @example
 * validateEmail('user@example.com') // true
 * validateEmail('invalid-email') // false
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Handle saving form data by calling the save callback and closing the modal
 * This is a common pattern for modal forms
 *
 * @param onSave - Optional callback function to save the data
 * @param data - The data to save
 * @param onClose - Function to close the modal
 *
 * @example
 * handleSave(onSave, formData, onClose)
 */
export function handleSave<T>(
  onSave: ((data: T) => void) | undefined,
  data: T,
  onClose: () => void
): void {
  if (onSave) {
    onSave(data);
  }
  onClose();
}

export function mapUserPayload(data: BackendUsersMeResponse): MePayload {
  return {
    id: data.id ?? '',
    first_name: data.first_name ?? '',
    last_name: data.last_name ?? '',
    email: data.email ?? '',
    phones: data.phones ?? [],
    avatar_url: data.avatar_url ?? null,
    is_active: data.is_active ?? false,
    is_staff: data.is_staff ?? false,
    is_superuser: data.is_superuser ?? false,
    permissions: data.permissions ?? [],
    workspace: data.workspace ?? null,
    parameters: data.parameters ?? {},
  };
}

export function handleAxiosError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as any;

    const detail = data?.detail ?? data?.message ?? data?.error;
    if (typeof detail === 'string' && detail.trim()) return detail;

    if (typeof data === 'string' && data.trim()) return data;

    if (data && typeof data === 'object') {
      try {
        const asJson = JSON.stringify(data);
        if (asJson && asJson !== '{}' && asJson !== '[]') return asJson;
      } catch {
        // ignore
      }
    }

    const status = err.response?.status;
    if (typeof status === 'number') return `${fallback} (HTTP ${status})`;

    const msg = typeof err.message === 'string' ? err.message.trim() : '';
    return msg || fallback;
  }
  return 'Erreur réseau.';
}

/**
 * Configuration centralisée de l'API
 */
export const API_CONFIG = {
  baseUrl: (
    (typeof process !== 'undefined' ? process.env.CARPULSE_CLIENT_API : undefined) ||
    (typeof process !== 'undefined' ? process.env.WEB_SOCKET_API_URL : undefined) ||
    ''
  ).replace(/\/$/, ''),
} as const;
/**
 * Récupère l'URL de base de l'API
 * @throws {Error} Si l'URL n'est pas configurée
 */
export function getApiBaseUrl(): string {
  if (!API_CONFIG.baseUrl) {
    const nodeEnv = typeof process !== 'undefined' ? process.env.NODE_ENV : undefined;
    if (nodeEnv === 'development') {
      // Intentionally keep the function pure in environments like Storybook (browser).
    }
    throw new Error(
      'API URL is not configured. Please set CARPULSE_CLIENT_API environment variable.'
    );
  }
  return API_CONFIG.baseUrl;
}

export function parseDataDate(str: string): Date | null {
  if (!str) return null;
  const trimmed = str.trim();
  /** ISO `YYYY-MM-DD` (souvent renvoyé par l’API) : date en **local**, évite le décalage UTC. */
  const isoDay = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(trimmed);
  if (isoDay) {
    const y = Number(isoDay[1]);
    const m = Number(isoDay[2]) - 1;
    const d = Number(isoDay[3]);
    const dt = new Date(y, m, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  if (/^\d{2}\/\d{2}$/.test(str)) {
    const [d, m] = str.split('/').map(Number);
    const now = new Date();
    return new Date(now.getFullYear(), m - 1, d);
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split('/').map(Number);
    return new Date(y, m - 1, d);
  }
  const parsed = new Date(str);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Formatte la date au format court (ex: "10 Nov 2025")
 */
export function formatDateShort(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
    .format(date)
    .replace('.', '');
}

/**
 * Formatte la date au format long (ex: "10 novembre 2025")
 */
export function formatDateLong(dateString?: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Formatte une date en temps relatif (ex: "À l'instant", "il y a 5 min", "il y a 2j")
 */
export function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `il y a ${diffMins} min`;
  if (diffHours < 24) return `il y a ${diffHours}h`;
  if (diffDays < 7) return `il y a ${diffDays}j`;

  return date.toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' });
}

export function smartFormatY(value: number): string {
  if (value === 0) return '0';
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${Math.round(value / 1_000)}k`;
  return String(Math.round(value));
}

export function aggregateData(
  data: ChartDataPoint[],
  period: string,
  calendarRange?: { startDate: Date | null; endDate: Date | null }
) {
  const parsed: Array<{ date: Date; value: number }> = data
    .map((d) => ({ date: parseDataDate(d.month), value: d.value }))
    .filter((d): d is { date: Date; value: number } => d.date !== null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let rangeStart: Date;
  let rangeEnd: Date = new Date(today);
  let bucketType:
    | 'day-of-week'
    | 'daily-sparse'
    | 'weekly'
    | 'monthly'
    | 'custom-sparse';

  if (calendarRange?.startDate && calendarRange?.endDate) {
    rangeStart = new Date(calendarRange.startDate);
    rangeEnd = new Date(calendarRange.endDate);
    rangeStart.setHours(0, 0, 0, 0);
    rangeEnd.setHours(0, 0, 0, 0);
    const diffDays =
      Math.round((rangeEnd.getTime() - rangeStart.getTime()) / 86_400_000) + 1;
    bucketType =
      diffDays <= 7
        ? 'day-of-week'
        : diffDays <= 60
          ? 'daily-sparse'
          : diffDays <= 120
            ? 'weekly'
            : 'monthly';
  } else {
    switch (period) {
      case '7 derniers jours':
        rangeStart = new Date(today);
        rangeStart.setDate(today.getDate() - 6);
        bucketType = 'day-of-week';
        break;
      case 'Ce mois-ci': {
        rangeStart = new Date(today.getFullYear(), today.getMonth(), 1);
        bucketType = 'daily-sparse';
        break;
      }
      case '30 derniers jours':
        rangeStart = new Date(today);
        rangeStart.setDate(today.getDate() - 29);
        bucketType = 'daily-sparse';
        break;
      case '3 derniers mois':
        rangeStart = new Date(today);
        rangeStart.setMonth(today.getMonth() - 3);
        bucketType = 'monthly';
        break;
      case '6 derniers mois':
      default:
        rangeStart = new Date(today);
        rangeStart.setMonth(today.getMonth() - 6);
        bucketType = 'monthly';
        break;
    }
  }

  const inRange = parsed.filter(
    (d) => d.date >= rangeStart && d.date <= rangeEnd
  );

  // Évite d'ajouter des jours "vides" en fin de série (ex: aujourd'hui sans point API),
  // ce qui crée une chute artificielle à 0 sur la courbe.
  const effectiveRangeEnd =
    (bucketType === 'day-of-week' || bucketType === 'daily-sparse') &&
    inRange.length > 0
      ? new Date(Math.max(...inRange.map((d) => d.date.getTime())))
      : rangeEnd;

  const sumInInterval = (from: Date, to: Date): number =>
    inRange
      .filter((d) => d.date >= from && d.date < to)
      .reduce((acc, d) => acc + d.value, 0);

  if (bucketType === 'day-of-week') {
    const labels: string[] = [];
    const values: number[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(rangeStart);
      day.setDate(rangeStart.getDate() + i);
      if (day > effectiveRangeEnd) break;
      labels.push(FR_DAYS_SHORT[day.getDay()]);
      const dayEnd = new Date(day);
      dayEnd.setDate(day.getDate() + 1);
      values.push(sumInInterval(day, dayEnd));
    }
    return { labels, values };
  }

  if (bucketType === 'daily-sparse') {
    const totalDays =
      Math.round((rangeEnd.getTime() - rangeStart.getTime()) / 86_400_000) + 1;
    const labelEvery = Math.ceil(totalDays / 6);
    const labels: string[] = [];
    const values: number[] = [];

    for (let i = 0; i < totalDays; i++) {
      const day = new Date(rangeStart);
      day.setDate(rangeStart.getDate() + i);
      const dayEnd = new Date(day);
      dayEnd.setDate(day.getDate() + 1);
      const v = sumInInterval(day, dayEnd);
      values.push(v);

      if (i % labelEvery === 0 || i === totalDays - 1) {
        labels.push(
          `${String(day.getDate()).padStart(2, '0')}/${String(
            day.getMonth() + 1
          ).padStart(2, '0')}`
        );
      } else {
        labels.push('');
      }
    }
    return { labels, values };
  }

  if (bucketType === 'weekly') {
    const labels: string[] = [];
    const values: number[] = [];
    const startMonday = new Date(rangeStart);
    const dow = startMonday.getDay();
    const offsetToMon = dow === 0 ? -6 : 1 - dow;
    startMonday.setDate(startMonday.getDate() + offsetToMon);

    let weekStart = new Date(startMonday);
    while (weekStart <= rangeEnd) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 7);
      values.push(sumInInterval(weekStart, weekEnd));
      labels.push(
        `${String(weekStart.getDate()).padStart(2, '0')}/${String(
          weekStart.getMonth() + 1
        ).padStart(2, '0')}`
      );
      weekStart = new Date(weekEnd);
    }
    return { labels, values };
  }

  // monthly
  const labels: string[] = [];
  const values: number[] = [];
  let cur = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
  const endMonth = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth() + 1, 1);
  while (cur < endMonth) {
    const nextMonth = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    values.push(sumInInterval(cur, nextMonth));
    labels.push(FR_MONTHS_SHORT[cur.getMonth()]);
    cur = nextMonth;
  }
  return { labels, values };
}

/**
 * Surbrillance de l’axe X (jour / mois) alignée sur le filtre de période du ChartCard.
 */
export function resolveChartXAxisHighlightMode(
  mode: 'weekday' | 'month' | 'none' | 'auto',
  period: string,
  calendarRange: { startDate: Date | null; endDate: Date | null },
): 'weekday' | 'month' | 'none' {
  if (mode !== 'auto') return mode;
  const start = calendarRange.startDate;
  const end = calendarRange.endDate;
  if (start && end) {
    const rs = new Date(start);
    const re = new Date(end);
    rs.setHours(0, 0, 0, 0);
    re.setHours(0, 0, 0, 0);
    const diffDays =
      Math.round((re.getTime() - rs.getTime()) / 86_400_000) + 1;
    if (diffDays <= 7) return 'weekday';
    if (diffDays > 90) return 'month';
    return 'none';
  }
  if (period === '7 derniers jours') return 'weekday';
  if (period === '6 derniers mois' || period === '3 derniers mois') {
    return 'month';
  }
  return 'none';
}

export async function getAccessToken(): Promise<string | null> {
  const { cookies } = await import('next/headers');
  const { ACCESS_TOKEN } = await import('@/features/auth/constant');
  return (await cookies()).get(ACCESS_TOKEN)?.value ?? null;
}

export function mapApiActivity(item: ApiActivity): Activity {
  return {
    id: item.id,
    createdAt: item.timestamp,
    message: item.action,
    actorRole: item.user?.role ?? '',
    actorName: item.user?.name ?? '',
  };
}
export function mapApiNotifToFront(
  data: ApiNotificationSettings
): NotificationSettings {
  const mapped: Partial<NotificationSettings> = {};

  // Map des events
  data.events.forEach((e) => {
    switch (e.id) {
      case 'deal_sent':
        mapped.dealSent = e.enabled ?? true;
        break;
      case 'deal_indexed':
        mapped.dealIndexed = e.enabled ?? true;
        break;
      case 'meeting_scheduled':
        mapped.meetingScheduled = e.enabled ?? true;
        break;
      case 'prospect_converted':
        mapped.prospectConverted = e.enabled ?? true;
        break;
      case 'critical_admin_action':
        mapped.criticalAdminAction = e.enabled ?? true;
        break;
    }
  });

  // Map des channels
  data.channels.forEach((c) => {
    switch (c.id) {
      case 'email':
        mapped.email = c.enabled ?? true;
        break;
      case 'integrations':
        mapped.integrations = c.enabled ?? true;
        break;
    }
  });

  // Retour avec valeurs par défaut pour éviter undefined
  return {
    dealSent: mapped.dealSent ?? true,
    dealIndexed: mapped.dealIndexed ?? true,
    meetingScheduled: mapped.meetingScheduled ?? true,
    prospectConverted: mapped.prospectConverted ?? true,
    criticalAdminAction: mapped.criticalAdminAction ?? true,
    email: mapped.email ?? true,
    integrations: mapped.integrations ?? true,
  };
}
